import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState, useRef, memo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useGlobalStore } from "../../api/globalStores";
import { Module } from "../../api/module";
import noVideo from "../../assets/no-video.png";
import IconMic from "../../Common/Icons/IconMic";
import IconRecord from "../../Common/Icons/IconRecord";
import IconShareScreen from "../../Common/Icons/IconShareScreen";
import IconSwitch from "../../Common/Icons/IconSwitch";
import Modal from "../../Common/Utility/Modal";
import EngagementPanel from "../Components/EngagementPanelEditor/EngagementPanel";
import SlideDrawerItem from "../Components/SlideDrawerItem";
import { StudentVideoCard } from "../Components/StudentVideoCard";
import Table from "../Components/Table/Table";
import TeacherFeedbackForm from "../Components/TeacherFeedbackForm";
import {
  HMSPeer,
  selectPeers,
  selectLocalPeer,
  selectRemotePeers,
  useHMSActions,
  useHMSStore,
  useVideo,
  useAVToggle,
  HMSChangeMultiTrackStateParams,
  HMSRoomState,
  selectSpeakers,
  selectIsPeerAudioEnabled,
  selectIsLocalScreenShared,
  selectIsConnectedToRoom,
  selectRoomState,
  useCustomEvent,
  useDevices,
  DeviceType,
} from "@100mslive/react-sdk";
import PreventRenderOnSmallScreen from "../../Common/Utility/PreventRenderOnSmallScreens";
import { supabase } from "../../api/supabase/client";
import { useHMSExceptionHandler } from "../../hooks/useHMSExceptionHandler";
import BrijLoadingIndicator from "../../Common/Misc/BrijLoadingIndicator";
import {
  resumeSharedAudioContext,
  unlockAudio,
} from "../../util/sharedAudioContext";

interface ITeacherLesson { }

export default observer(TeacherLesson);

const logger = console;

// iOS device and browser detection utilities
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (
    navigator.userAgent.includes("Mac") && "ontouchend" in document
  );
};

const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

const isChromeOnIOS = () => {
  return isIOS() && /CriOS/i.test(navigator.userAgent);
};

// Permission management for iOS
let permissionGranted = {
  microphone: false,
  camera: false
};

const checkMediaPermissions = async () => {
  if (!navigator.permissions) return { microphone: false, camera: false };

  try {
    const microphonePermission = await navigator.permissions.query({ name: 'microphone' as any });
    const cameraPermission = await navigator.permissions.query({ name: 'camera' as any });

    permissionGranted.microphone = microphonePermission.state === 'granted';
    permissionGranted.camera = cameraPermission.state === 'granted';

    return {
      microphone: microphonePermission.state === 'granted',
      camera: cameraPermission.state === 'granted'
    };
  } catch (error) {
    logger.error('Error checking permissions:', error);
    return { microphone: false, camera: false };
  }
};

const requestMediaPermissions = async (force = false) => {
  if (!isIOS()) return true;

  try {
    // Check existing permissions first
    const existingPermissions = await checkMediaPermissions();

    // If we already have permissions and not forcing, return early
    if (!force && existingPermissions.microphone && existingPermissions.camera) {
      logger.log('Permissions already granted');
      return true;
    }

    // For Safari, we need user gesture to request permissions
    if (isSafari() && !force) {
      logger.log('Safari detected - permissions will be requested on user interaction');
      return false; // Let the UI handle permission requests
    }

    // Request permissions
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true
    });

    // Stop the stream immediately - we just needed it for permissions
    stream.getTracks().forEach(track => track.stop());

    permissionGranted.microphone = true;
    permissionGranted.camera = true;

    logger.log('Media permissions granted');
    return true;
  } catch (error) {
    logger.error('Failed to get media permissions:', error);
    return false;
  }
};

/**
 * Initialise audio for iOS using the shared AudioContext singleton.
 */
const initializeIOSAudio = async () => {
  if (!isIOS()) return;
  try {
    await unlockAudio();
    logger.log("iOS audio session initialized (shared context)");
  } catch (error) {
    logger.error("Failed to initialize iOS audio:", error);
  }
};

function TeacherLesson(props: ITeacherLesson) {
  const globalStores = useGlobalStore();
  const navigate = useNavigate();

  // Release heap-heavy lesson state when the component unmounts
  useEffect(() => {
    return () => {
      globalStores.curTalkingParty.cleanupSessionMemory();
    };
  }, []);

  //modal open states
  const [openSwitchLesson, setOpenSwitchLesson] = useState<boolean>(false);
  const [openTeacherFeedback, setOpenTeacherFeedback] =
    useState<boolean>(false);

  const [modules, setModules] = useState<Module[]>([]);
  const hmsActions = useHMSActions();
  const teacherPeer = useHMSStore(selectLocalPeer);
  const peers = useHMSStore(selectRemotePeers);
  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio, toggleVideo } =
    useAVToggle();
  const isLocalScreenShared = useHMSStore(selectIsLocalScreenShared);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const roomState = useHMSStore(selectRoomState);
  const isReconnecting = roomState === HMSRoomState.Reconnecting;
  const [showRestartButton, setShowRestartButton] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isReconnecting) {
      timer = setTimeout(() => {
        setShowRestartButton(true);
      }, 10000); // Show restart button after 10s of reconnecting
    } else {
      setShowRestartButton(false);
    }
    return () => clearTimeout(timer);
  }, [isReconnecting]);

  const handleManualRestart = async () => {
    try {
      toast.loading("Restarting connection...");
      await hmsActions.leave();
      await globalStores.curTalkingParty.joinRoom();
      toast.success("Connection restarted");
    } catch (error) {
      logger.error("Manual restart failed", error);
      toast.error("Restart failed. Please refresh the page.");
    }
  };

  useHMSExceptionHandler();

  const [studentFeedOverride, setStudentFeedOverride] = useState<
    string | undefined
  >();
  const [studentFeedOverrideName, setStudentFeedOverrideName] = useState<
    string | undefined
  >();
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    globalStores.curTalkingParty.setHMSActions(hmsActions);
  }, [hmsActions, globalStores.curTalkingParty]);

  const startJoinRoom = async () => {
    setJoinError(null);
    try {
      // Join room
      if (!globalStores.curTalkingParty.talkingParty.id) {
        return navigate("/teacher/calendar");
      }

      if (!globalStores.curTalkingParty.twilioToken) {
        logger.log("No token, attempting to fetch...");
        await globalStores.curTalkingParty.getAccessToken();
      }

      const room = await globalStores.curTalkingParty.joinRoom();
      if (!room) {
        throw new Error("Failed to join room");
      }
    } catch (error: any) {
      logger.error('Failed to join room:', error);
      setJoinError(error.message || "Unknown error");
      toast.error("Failed to join the class. Please check your connection.");
    }
  };

  // iOS-specific: Handle permissions and audio initialization (non-blocking)
  useEffect(() => {
    const run = async () => {
      // Load modules
      setModules(await globalStores.teacherStore.getModules());

      if (isIOS()) {
        try {
          logger.log("Detected iOS device, initialising audio...");
          initializeIOSAudio();
          if (isChromeOnIOS()) {
            requestMediaPermissions(true);
          }
        } catch (error) {
          logger.error("iOS initialization failed:", error);
        }
      }

      startJoinRoom();
    };

    run();
  }, [globalStores.curTalkingParty]);

  const panelListRef = useRef<HTMLDivElement>(null);
  const hasObservedRef = useRef(false);
  useEffect(() => {
    if (isConnected && !hasObservedRef.current) {
      hmsActions.sessionStore.observe([
        "currentEngagementPanel",
        "showAnswerForEngagementPanel",
        "currentEngagementPanelId",
      ]);
      hasObservedRef.current = true;
    } else if (!isConnected) {
      hasObservedRef.current = false;
    }
  }, [hmsActions, isConnected]);

  // Use Supabase Realtime to handle updates for engagement panels
  useEffect(() => {
    const roomId = globalStores.curTalkingParty.talkingParty.id;
    if (!isConnected || !roomId) return;

    // 1. Supabase Realtime listener
    const channel = supabase
      .channel(`room-${roomId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "TalkingPartyRoom",
          filter: `id=eq.${roomId}`,
        },
        (payload) => {
          logger.log("Realtime update received", payload.new);
          // If the module ID changed in the payload, we can potentially trigger updates even faster
          globalStores.curTalkingParty.pullDetails();
        }
      )
      .subscribe();

    // 2. Polling fallback (Crucial for China where Realtime often disconnects)
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        globalStores.curTalkingParty.pullDetails();
      }
    }, 15000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [isConnected, globalStores.curTalkingParty.talkingParty.id]);

  // Initial module loading
  useEffect(() => {
    if (
      globalStores.curTalkingParty.talkingParty.module &&
      globalStores.curTalkingParty.talkingParty.module !==
      globalStores.curModule.module.id
    ) {
      globalStores.curModule.init(
        globalStores.curTalkingParty.talkingParty.module
      );
      panelListRef.current?.scrollTo({ top: 0 });
    }
  }, [globalStores.curTalkingParty.talkingParty.module]);

  //provides a toast whenever microphone state changes
  useEffect(() => {
    isConnected &&
      toast.success(`Microphone ${isLocalAudioEnabled ? "On" : "Off"}`);
  }, [isLocalAudioEnabled]);

  //provides a toast whenever camera state changes
  useEffect(() => {
    isConnected &&
      toast.success(`Camera ${isLocalVideoEnabled ? "On" : "Off"}`);
  }, [isLocalVideoEnabled]);

  //provides a toast whenever sreen share state changes
  useEffect(() => {
    isConnected &&
      toast.success(`Screen Share ${isLocalScreenShared ? "On" : "Off"}`);
  }, [isLocalScreenShared]);

  // Consolidate synchronization logic for engagement panels (Solely following Supabase)
  useEffect(() => {
    const supabaseEPanelID = globalStores.curTalkingParty.talkingParty.currentEngagementPanel;
    if (supabaseEPanelID && supabaseEPanelID !== globalStores.curEPanel.engagementPanel.id) {
      const preloadedPanel = globalStores.curModule.engagementPanelsLoaded.find(
        (p) => p.engagementPanel.id === supabaseEPanelID
      );

      if (preloadedPanel) {
        globalStores.curEPanel.initFromInput(preloadedPanel, {
          skipRefresh: true,
        });
      } else {
        globalStores.curEPanel.init(supabaseEPanelID);
      }
    }
  }, [globalStores.curTalkingParty.talkingParty.currentEngagementPanel, globalStores.curEPanel.engagementPanel.id]);

  const showAnswerForEngagementPanel =
    globalStores.curTalkingParty.talkingParty.showAnswerForEngagementPanel;

  useEffect(() => {
    isConnected &&
      toast.success(
        showAnswerForEngagementPanel ? "Answer Shown" : "Answer Hidden"
      );
  }, [showAnswerForEngagementPanel]);

  useEffect(() => {
    if (isConnected && globalStores.teacherStore.staffDetail.displayName) {
      hmsActions.changeName(globalStores.teacherStore.staffDetail.displayName);
    }
  }, [isConnected, globalStores.teacherStore.staffDetail.displayName]);

  const { sendEvent: sendLowerHandEvent } = useCustomEvent({
    type: "LOWER_HAND",
  });

  const { sendEvent: sendMuteAllStudentsEvent } = useCustomEvent({
    type: "MUTE_ALL_STUDENTS",
  });

  //Detect Key Presses for Switching Between Panels
  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const curPosition = globalStores.curModule.engagementPanels.findIndex(
          ({ id }) => id === globalStores.curEPanel.engagementPanel.id
        );

        //if it's the first or last EPanel, do nothing
        if (
          (curPosition === 0 && event.key === "ArrowUp") ||
          (curPosition === globalStores.curModule.engagementPanels.length &&
            event.key === "ArrowDown")
        ) {
          return;
        }

        //means not found (i.e. none of them are selected yet)
        if (curPosition < 0) {
          return; //do nothing also
        }

        const newPosition =
          event.key === "ArrowDown" ? curPosition + 1 : curPosition - 1;

        const newEPanelID =
          globalStores.curModule.engagementPanels[newPosition].id;

        setCurrentEngagmentPanelID(newEPanelID!);
        setShowAnswerForEngagementPanel(false);
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  const handleClearHands = useCallback(async () => {
    // await globalStores.curTalkingParty.clearHands();
    // const clearHandsPromise = Promise.all(
    //   peers.map(({ id }) => {
    //     return hmsActions.sendDirectMessage("Lower Hand!", id, "LOWER_HAND");
    //   })
    // );

    // toast.promise(clearHandsPromise, {
    //   loading: "Clearing Hands",
    //   success: "Hands Cleared",
    //   error: (e) => e.message,
    // });

    // hmsActions.sendGroupMessage("LOWER_HAND", ["student"], "LOWER_HAND");
    sendLowerHandEvent({});
    toast.success("Lowering Hands Now");
  }, [hmsActions, peers]);

  //TODO: Enscapulsate in useCallback hook
  const toggleCamera = async () => {
    if (!toggleVideo) {
      throw new Error("You do not have permission to unmute video");
    }

    // For Safari on iOS, ensure we have permissions before toggling (non-blocking)
    if (isSafari() && isIOS()) {
      try {
        const hasPermissions = await requestMediaPermissions(true);
        if (!hasPermissions) {
          toast.error("Camera permission required. Please allow camera access.");
          return;
        }
      } catch (error) {
        logger.error('Camera permission check failed:', error);
        // Continue anyway to avoid blocking the toggle
      }
    }

    toggleVideo();
  };

  //TODO: Enscapulsate in useCallback hook
  const toggleMic = async () => {
    if (!toggleAudio) {
      throw new Error("You do not have permission to unmute audio");
    }

    // For Safari on iOS, ensure we have permissions before toggling (non-blocking)
    if (isSafari() && isIOS()) {
      try {
        const hasPermissions = await requestMediaPermissions(true);
        if (!hasPermissions) {
          toast.error("Microphone permission required. Please allow microphone access.");
          return;
        }
      } catch (error) {
        logger.error('Microphone permission check failed:', error);
        // Continue anyway to avoid blocking the toggle
      }
    }

    toggleAudio();
  };

  //TODO: Enscapulsate in useCallback hook
  const toggleShareScreen = async () => {
    // const newState = !globalStores.curTalkingParty.screenShared;
    // globalStores.curTalkingParty.toggleShareScreen(newState);
    // toast.success(`Share Screen ${newState ? "On" : "Off"}`);
    try {
      await hmsActions.setScreenShareEnabled(!isLocalScreenShared);
    } catch (error) {
      // an error will be thrown if user didn't give access to share screen
      console.error(error);
    }
  };

  //TODO: Enscapulsate in useCallback hook
  const muteAll = async () => {
    const options: HMSChangeMultiTrackStateParams = {
      enabled: false, // false to mute, true to unmute
      roles: ["student"], // optional, array of roles to mute, mutes everyone if not passed
      type: "audio", // optional, audio/video, mutes both if not passed
      source: "regular", // optional, mutes all sources(regular, screen etc.) if not passed
    };

    const unmuteAllPromise = hmsActions.setRemoteTracksEnabled(options);
    toast.promise(unmuteAllPromise, {
      success: "All students muted",
      loading: "Muting all students",
      error: (e) => e.message,
    });
    sendMuteAllStudentsEvent({});
  };

  // Debounce rapid panel switching so only the last switch within 150ms fires the DB update.
  const panelSwitchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up debounce timer on unmount to prevent firing on a cleaned-up store.
  useEffect(() => {
    return () => {
      if (panelSwitchTimerRef.current) clearTimeout(panelSwitchTimerRef.current);
    };
  }, []);

  const setCurrentEngagmentPanelID = useCallback((EPanelID: number) => {
    // Update local state + arm the realtime guard immediately for instant UI feedback
    globalStores.curTalkingParty.setEPanelLocal(EPanelID);

    if (panelSwitchTimerRef.current) {
      clearTimeout(panelSwitchTimerRef.current);
    }
    panelSwitchTimerRef.current = setTimeout(async () => {
      try {
        await globalStores.curTalkingParty.setEPanel(EPanelID);
      } catch (error) {
        console.error("Teacher set error:", error);
      }
    }, 150);
  }, [globalStores.curTalkingParty]);

  //TODO: Enscapulsate in useCallback hook
  const setShowAnswerForEngagementPanel = async (showAnswer: boolean) => {
    try {
      await globalStores.curTalkingParty.showAnswer(showAnswer);
    } catch (error) {
      console.error(error);
    }
  };

  //TODO: Enscapulsate in useCallback hook
  const toggleShowAnswerForEngagementPanel = async () => {
    try {
      const newState = !showAnswerForEngagementPanel;
      await setShowAnswerForEngagementPanel(newState);
    } catch (error) {
      console.error(error);
    }
  };

  const onMouseEnterStudentFeed = useCallback(
    (trackID?: string, name?: string) => {
      setStudentFeedOverride(trackID);
      setStudentFeedOverrideName(name);
    },
    [setStudentFeedOverride, setStudentFeedOverrideName]
  );

  const onMouseLeaveStudentFeed = useCallback(() => {
    setStudentFeedOverride(undefined);
    setStudentFeedOverrideName(undefined);
  }, [setStudentFeedOverride, setStudentFeedOverrideName]);

  const tableRowData = useMemo(() => {
    return modules.map((module) => {
      return {
        level: String(module.level.hskDifficulty ?? 0),
        topic: module.topic.name ?? "",
        module: module.module.name ?? "",
        fullModule: module,
      };
    });
  }, [modules]);

  return (
    <PreventRenderOnSmallScreen>
      {isReconnecting && (
        <div className="fixed inset-0 z-[100] bg-brij-Mud bg-opacity-80 flex flex-col items-center justify-center text-brij-White p-10 text-center">
          <div className="w-16 h-16 border-4 border-brij-Peach border-t-transparent rounded-full animate-spin mb-4" />
          <h2 className="text-heading2 font-bold mb-2">Connection Unstable</h2>
          <p className="text-large opacity-80 max-w-md">
            We are automatically trying to reconnect you to the classroom. Please stay on this page.
          </p>
          {showRestartButton && (
            <button
              onClick={handleManualRestart}
              className="mt-6 px-6 py-2 bg-brij-Orange text-white rounded-lg font-bold hover:bg-opacity-90 transition-all shadow-lg"
            >
              Restart Connection Now
            </button>
          )}
        </div>
      )}
      {!isConnected && !isReconnecting && (
        <div className="fixed inset-0 z-[100] bg-brij-Mud flex flex-col items-center justify-center text-brij-White p-10 text-center">
          {!joinError ? (
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="scale-150">
                <BrijLoadingIndicator />
              </div>
              <h2 className="text-heading3 font-bold mb-2">Joining classroom...</h2>
            </div>
          ) : (
            <>
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-heading2 font-bold mb-2">Failed to Join</h2>
              <p className="text-large opacity-80 max-w-md mb-6">
                Couldn't establish a connection.
              </p>
              <div className="bg-brij-Black bg-opacity-30 p-4 rounded-lg mb-6 text-xsmall font-mono break-all max-w-lg">
                Error: {joinError}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={startJoinRoom}
                  className="px-8 py-3 bg-brij-Orange text-white rounded-lg font-bold hover:bg-opacity-90 transition-all shadow-lg"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate("/teacher/calendar")}
                  className="px-8 py-3 bg-brij-Grey bg-opacity-20 text-white rounded-lg font-bold hover:bg-opacity-30 transition-all"
                >
                  Back to Calendar
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <Modal
        open={openSwitchLesson}
        onClose={() => {
          setOpenSwitchLesson(false);
        }}
      >
        <div className="bg-brij-White flex flex-col gap-2 p-6 rounded-xl">
          <div className="inline-flex">
            <div className="flex w-[552px] items-start justify-between overflow-clip p-2.5">
              <p className="font-poppins text-xlarge text-brij-Mud font-medium leading-[1.4]">
                Switch Lesson To
              </p>
            </div>
          </div>
          <div className="w-full h-96 p-2 flex flex-col">
            <Table
              rowData={tableRowData}
              //@ts-ignore
              colData={sampleColumns}
              onClickRow={(row) => {
                toast.promise(
                  (async () => {
                    const moduleId = row.fullModule.module.id;
                    const epanelId = null;

                    if (
                      moduleId &&
                      globalStores.curModule.module.id !== +moduleId
                    ) {
                      // const { topic, module, level } = row;
                      // updateModuleInSessionStore({ topic, module, level });
                      await globalStores.curTalkingParty.setModule(+moduleId);
                    }

                    if (
                      epanelId !== null &&
                      globalStores.curEPanel.engagementPanel.id !== +epanelId
                    ) {
                      await globalStores.curTalkingParty.setEPanel(+epanelId);
                    }

                    setOpenSwitchLesson(false);
                  })(),
                  {
                    loading: "Loading Module and Engagement Panel",
                    success: "Loaded",
                    error: (e: any) => e.message,
                  }
                );
              }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={openTeacherFeedback}
        onClose={() => {
          setOpenTeacherFeedback(false);
        }}
      >
        <TeacherFeedbackForm formTriggerLocation="TeacherLesson" />
      </Modal>
      <div className="flex w-full h-full">
        {/* LHS Side Panel to show Module and its EPanels */}
        <div className="p-5 gap-3 bg-brij-Mud flex flex-col h-full w-[180px]">
          <div className="inline-flex flex-col text-brij-White gap-1">
            <div className="text-xlarge font-semibold">
              Level {globalStores.curModule.level.hskDifficulty || (globalStores.curTalkingParty.module as any)?.topic?.level?.hskDifficulty}
            </div>
            <span className="inline-flex gap-2 text-normal font-semibold">
              <div>⮡ </div>
              <div>{globalStores.curModule.topic.name || (globalStores.curTalkingParty.module as any)?.topic?.name}</div>
            </span>
            <p className="text-small">
              {globalStores.curModule.isLoading && !globalStores.curTalkingParty.module?.name
                ? "Loading Module..."
                : (globalStores.curModule.module.name || globalStores.curTalkingParty.module?.name || "No Module Name")}
            </p>
          </div>
          <button
            onClick={() => {
              setOpenSwitchLesson(true);
            }}
            className="flex items-center w-full gap-2 hover:bg-brij-DarkGrey hover:bg-opacity-25 py-2 px-3 rounded-xl"
          >
            <div className="h-[20px] w-[20px] drop-shadow-lg fill-brij-White rounded-2xl">
              <IconSwitch />
            </div>
            <div className="text-normal font-bold text-brij-White">Switch</div>
          </button>
          <div className="h-1 w-full bg-brij-Grey bg-opacity-40" />
          <div className="flex items-center justify-between w-full">
            <div className="flex-col items-start justify-start w-full">
              <p className="text-large font-semibold text-brij-White">Panels</p>
              {/* <p className="text-xsmall text-brij-White">Drag to reorder</p> */}
            </div>
          </div>
          <div ref={panelListRef} className="h-full w-full flex flex-col items-center gap-4 overflow-y-scroll pr-2">
            {globalStores.curModule.engagementPanels.map((ePanel, idx) => {
              return (
                <SlideDrawerItem
                  id={ePanel.id!}
                  key={ePanel.id}
                  position={idx}
                  epType={ePanel.type ?? "MCQ"}
                  curEPanelID={globalStores.curEPanel.engagementPanel.id}
                  onClick={() => {
                    setCurrentEngagmentPanelID(ePanel.id!);
                    return setShowAnswerForEngagementPanel(false);
                  }}
                />
              );
            })}
            {/* TODO: Figure out if we can put back the Add button later on */}
            {/* <div className="p-4 ml-4 grid place-items-center">
              <AddButton />
            </div> */}
          </div>

          <button
            onClick={() => {
              setOpenTeacherFeedback(true);
            }}
            className="bg-brij-Peach inline-flex text-normal text-brij-White justify-center items-center py-2 px-6 shadow-2xl rounded-xl font-semibold"
          >
            End Class
          </button>
        </div>
        {/* Middle LGP Preview */}
        <div className="p-4">
          <EngagementPanel mode={showAnswerForEngagementPanel ? "SHOW_ANSWER" : "SETUP"} />
        </div>
        {/* RHS Video Feeds + Controls */}
        <div className="flex flex-col gap-6 bg-brij-Mud text-brij-White p-6 flex-1 h-full overflow-x-clip">
          {/* Local Peer (i.e. Teacher) Video Feed + Teacher Controls */}
          <div className="flex justify-start items-start gap-6 overflow-clip w-full px-3">
            {/* Teacher Controls */}
            <div className="flex flex-col gap-3 py-3">
              <div className="flex flex-row gap-3 mb-1">
                {/* Record Button */}
                <button
                  onClick={toggleCamera}
                  className="h-[42px] w-[42px] drop-shadow-lg fill-brij-White bg-brij-Peach rounded-2xl p-2"
                >
                  {!isLocalVideoEnabled && (
                    <div className={"absolute inset-0 grid place-items-center"}>
                      <div className="bg-brij-White h-0.5 w-8 rotate-45" />
                    </div>
                  )}
                  <IconRecord />
                </button>
                {/* Mic Button */}
                <button
                  onClick={toggleMic}
                  className={`relative h-[42px] w-[42px] drop-shadow-lg fill-brij-White bg-brij-Orange rounded-2xl p-2 ${
                    globalStores.curTalkingParty.isTrackDegraded(
                      teacherPeer?.audioTrack
                    )
                      ? "animate-pulse border-2 border-white"
                      : ""
                  }`}
                >
                  {!isLocalAudioEnabled && (
                    <div className={"absolute inset-0 grid place-items-center"}>
                      <div className="bg-brij-White h-0.5 w-8 rotate-45" />
                    </div>
                  )}
                  <IconMic />
                </button>
                {/* Share Screen Button */}
                <button
                  onClick={toggleShareScreen}
                  className="relative h-[42px] w-[42px] drop-shadow-lg fill-brij-White bg-brij-Blue rounded-2xl p-2"
                >
                  {isLocalScreenShared && (
                    <div className="bg-brij-Blue inset-0 absolute rounded-2xl animate-pulse-scale-sinusoidal -z-10 opacity-50" />
                  )}
                  {!isLocalScreenShared && (
                    <div className={"absolute inset-0 grid place-items-center"}>
                      <div className="bg-brij-White h-0.5 w-8 rotate-45" />
                    </div>
                  )}
                  <IconShareScreen />
                </button>
              </div>
              {/* Mute All Button */}
              <button
                onClick={muteAll}
                className="text-center drop-shadow-lg bg-brij-Peach rounded-xl font-medium text-brij-White text-normal inline-flex items-center justify-center p-1.5"
              >
                Mute All
              </button>
              {/* Show Answer Button */}
              <button
                onClick={toggleShowAnswerForEngagementPanel}
                className="text-center drop-shadow-lg bg-brij-Gold rounded-xl font-medium text-brij-White text-normal inline-flex items-center justify-center p-1.5"
              >
                {showAnswerForEngagementPanel ? (
                  <>Hide Answer</>
                ) : (
                  <>Show Answer</>
                )}
              </button>
              {/* Clear Hands Button */}
              <button
                onClick={handleClearHands}
                className="text-center drop-shadow-lg bg-brij-Blue rounded-xl font-medium text-brij-White text-normal inline-flex items-center justify-center p-1.5"
              >
                Clear Hands
              </button>
            </div>
            {/* Local Peer (i.e. Teacher) Video Feed */}
            <div className="flex h-full max-h-[35vh] flex-1 relative justify-center items-center overflow-clip">
              {/* Show TeacherFeed if no StudentOverride */}
              {!studentFeedOverride && (
                <Video videoTrack={teacherPeer?.videoTrack} />
              )}
              {/* Work around to render StudentFeed without causing freezing */}
              {studentFeedOverride &&
                peers.map((peer) => {
                  if (studentFeedOverride !== peer.videoTrack) return <></>;
                  return <Video key={peer.id} videoTrack={peer.videoTrack} />;
                })}

              <div className="absolute object-cover bottom-0 left-0 p-6 w-full from-brij-Black bg-gradient-to-t rounded-xl">
                <div className="text-heading3 text-brij-White drop-shadow-xl font-semibold">
                  {studentFeedOverrideName ??
                    globalStores.teacherStore.staffDetail.displayName}
                </div>
              </div>
            </div>
          </div>
          {/* Remote Peers (i.e. Students) Dipslay */}
          <div className="h-full box-border w-full overflow-hidden flex">
            <div className="grid grid-cols-[repeat(2,_49%)] xl:grid-cols-[repeat(3,_32%)] auto-rows-[180px] box-border place-items-center gap-2 w-full h-full px-2 pb-20 overflow-y-scroll">
              {isConnected ? (
                peers.map((peer) => {
                  //LEFT OFF: Need to define child properly

                  if (!peer.videoTrack) return <></>;
                  return (
                    <>
                      <StudentVideoCard
                        key={peer.id}
                        participant={peer}
                        onMouseEnter={onMouseEnterStudentFeed}
                        onMouseLeave={onMouseLeaveStudentFeed}
                      />
                    </>
                  );
                })
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    </PreventRenderOnSmallScreen>
  );
}

function Video({ videoTrack }: { videoTrack?: string }) {
  const globalStores = useGlobalStore();
  const isDegraded = globalStores.curTalkingParty.isTrackDegraded(videoTrack);

  return (
    <div className="relative w-full h-full">
      <VideoElement videoTrack={videoTrack} />
      {isDegraded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-20 rounded-xl pointer-events-none">
          <div className="w-8 h-8 border-2 border-brij-Peach border-t-transparent rounded-full animate-spin mb-2" />
          <span className="text-xsmall font-bold text-white drop-shadow-md">
            Unstable
          </span>
        </div>
      )}
    </div>
  );
}

const VideoElement = memo(({ videoTrack }: { videoTrack?: string }) => {
  const { videoRef } = useVideo({
    trackId: videoTrack,
  });

  return (
    <video
      ref={videoRef}
      className="bg-brij-MudDark rounded-xl drop-shadow-xl w-full h-full overflow-clip aspect-video object-contain"
      autoPlay
      muted
      playsInline
    />
  );
});

const sampleColumns = [
  {
    Header: "Level",
    accessor: "level",
  },
  {
    Header: "Topic",
    accessor: "topic",
  },
  {
    Header: "Module",
    accessor: "module",
  },
];
